import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as ForecastsActions from './forecasts.actions';
import { ForecastsService } from '../../services/forecasts.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class ForecastsEffects {
  private actions$ = inject(Actions);
  private forecastsService = inject(ForecastsService);
  private router = inject(Router);
  private store = inject(Store);

  loadForecasts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastsActions.loadForecasts),
      switchMap(({ stockId }) =>
        this.forecastsService.getForecasts(stockId).pipe(
          map((response) => {
            const forecasts = response.data.items || response;
            return ForecastsActions.loadForecastsSuccess({ forecasts });
          }),
          catchError((error) => of(ForecastsActions.loadForecastsFailure({ error: error.message })))
        )
      )
    )
  );

  loadForecast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastsActions.loadForecast),
      switchMap(({ id }) =>
        this.forecastsService.getForecastById(id).pipe(
          map((response) => {
            const forecast = response.data || response;
            return ForecastsActions.loadForecastSuccess({ forecast });
          }),
          catchError((error) => of(ForecastsActions.loadForecastFailure({ error: error.message })))
        )
      )
    )
  );

  createForecast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastsActions.createForecast),
      switchMap(({ forecast }) =>
        this.forecastsService.createForecast(forecast).pipe(
          map((response) => {
            const createdForecast = response.data || response;

            return ForecastsActions.createForecastSuccess({ forecast: createdForecast });
          }),
          catchError((error) => {
            return of(ForecastsActions.createForecastFailure({ error: error.message }));
          })
        )
      )
    )
  );

  updateForecast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastsActions.updateForecast),
      switchMap(({ id, forecast }) =>
        this.forecastsService.updateForecast(id, forecast).pipe(
          map((response) => {
            const updatedForecast = response.data || response;

            return ForecastsActions.updateForecastSuccess({ forecast: updatedForecast });
          }),
          catchError((error) => {
            return of(ForecastsActions.updateForecastFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deleteForecast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForecastsActions.deleteForecast),
      switchMap(({ id }) =>
        this.forecastsService.deleteForecast(id).pipe(
          map(() => {
            return ForecastsActions.deleteForecastSuccess({ id });
          }),
          catchError((error) => {
            return of(ForecastsActions.deleteForecastFailure({ error: error.message }));
          })
        )
      )
    )
  );

  forecastSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ForecastsActions.createForecastSuccess,
          ForecastsActions.updateForecastSuccess,
          ForecastsActions.deleteForecastSuccess
        ),
        tap(() => {
          this.store.select(selectSelectedStock).subscribe((stock) => {
            this.router.navigate(['/stocks', 'view', stock?.id, 'forecast']);
          });
        })
      ),
    { dispatch: false }
  );
}
